import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import Icon from '../../../components/AppIcon';

const SpreadAnalysis = ({ selectedContract }) => {
  const [spreadData, setSpreadData] = useState([]);
  const [timeframe, setTimeframe] = useState('1H'); // '5M', '15M', '1H', '4H'
  const [viewType, setViewType] = useState('spread'); // 'spread', 'midprice', 'volatility'

  // Generate mock spread data
  const generateSpreadData = () => {
    const now = new Date();
    const data = [];
    const baseSpread = selectedContract?.includes('EUA') ? 0.05 : 0.05;
    const basePrice = selectedContract?.includes('EUA') ? 85.25 : 78.25;
    
    const intervals = {
      '5M': { count: 48, interval: 5 * 60 * 1000 },
      '15M': { count: 32, interval: 15 * 60 * 1000 },
      '1H': { count: 24, interval: 60 * 60 * 1000 },
      '4H': { count: 12, interval: 4 * 60 * 60 * 1000 }
    };

    const { count, interval } = intervals?.[timeframe];

    for (let i = count; i >= 0; i--) {
      const time = new Date(now.getTime() - (i * interval));
      const spread = baseSpread + (Math.random() - 0.5) * 0.02;
      const midPrice = basePrice + (Math.random() - 0.5) * 2;
      const volatility = Math.random() * 0.5 + 0.1;
      
      data?.push({
        time: time?.toLocaleTimeString('en-US', { 
          hour: '2-digit', 
          minute: '2-digit',
          hour12: false 
        }),
        timestamp: time,
        spread: Math.max(0.01, spread),
        midPrice: midPrice,
        volatility: volatility,
        bidPrice: midPrice - spread / 2,
        askPrice: midPrice + spread / 2,
        spreadBps: (spread / midPrice) * 10000 // basis points
      });
    }
    
    return data;
  };

  useEffect(() => {
    const updateSpreadData = () => {
      setSpreadData(generateSpreadData());
    };

    updateSpreadData();
    const interval = setInterval(updateSpreadData, 5000);
    return () => clearInterval(interval);
  }, [selectedContract, timeframe]);

  const formatPrice = (price) => {
    return selectedContract?.includes('EUA') ? `€${price?.toFixed(2)}` : `$${price?.toFixed(2)}`;
  };

  const getCurrentSpread = () => {
    if (spreadData?.length === 0) return null;
    const latest = spreadData?.[spreadData?.length - 1];
    return {
      spread: latest?.spread,
      spreadBps: latest?.spreadBps,
      midPrice: latest?.midPrice,
      volatility: latest?.volatility
    };
  };

  const getSpreadStats = () => {
    if (spreadData?.length === 0) return null;
    
    const spreads = spreadData?.map(d => d?.spread);
    const spreadBps = spreadData?.map(d => d?.spreadBps);
    
    return {
      min: Math.min(...spreads),
      max: Math.max(...spreads),
      avg: spreads?.reduce((a, b) => a + b, 0) / spreads?.length,
      minBps: Math.min(...spreadBps),
      maxBps: Math.max(...spreadBps),
      avgBps: spreadBps?.reduce((a, b) => a + b, 0) / spreadBps?.length
    };
  };

  const currentSpread = getCurrentSpread();
  const spreadStats = getSpreadStats();

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      const data = payload?.[0]?.payload;
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-elevated">
          <p className="text-sm font-medium text-popover-foreground mb-2">{label}</p>
          {viewType === 'spread' && (
            <>
              <p className="text-xs text-muted-foreground">
                Spread: <span className="font-mono text-foreground">{formatPrice(data?.spread)}</span>
              </p>
              <p className="text-xs text-muted-foreground">
                Basis Points: <span className="font-mono text-foreground">{data?.spreadBps?.toFixed(1)} bps</span>
              </p>
            </>
          )}
          {viewType === 'midprice' && (
            <>
              <p className="text-xs text-muted-foreground">
                Mid Price: <span className="font-mono text-foreground">{formatPrice(data?.midPrice)}</span>
              </p>
              <p className="text-xs text-muted-foreground">
                Bid: <span className="font-mono text-success">{formatPrice(data?.bidPrice)}</span>
              </p>
              <p className="text-xs text-muted-foreground">
                Ask: <span className="font-mono text-error">{formatPrice(data?.askPrice)}</span>
              </p>
            </>
          )}
          {viewType === 'volatility' && (
            <p className="text-xs text-muted-foreground">
              Volatility: <span className="font-mono text-foreground">{(data?.volatility * 100)?.toFixed(2)}%</span>
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-subtle">
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">Spread Analysis</h3>
          <div className="flex items-center gap-2">
            {/* Timeframe Selection */}
            <div className="flex items-center gap-1">
              {['5M', '15M', '1H', '4H']?.map(tf => (
                <button
                  key={tf}
                  onClick={() => setTimeframe(tf)}
                  className={`
                    px-2 py-1 text-xs rounded transition-colors duration-200
                    ${timeframe === tf 
                      ? 'bg-primary text-primary-foreground' 
                      : 'bg-muted text-muted-foreground hover:bg-muted/80'
                    }
                  `}
                >
                  {tf}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* View Type Selection */}
        <div className="flex items-center gap-2 mb-4">
          {[
            { key: 'spread', label: 'Spread', icon: 'BarChart3' },
            { key: 'midprice', label: 'Mid Price', icon: 'TrendingUp' },
            { key: 'volatility', label: 'Volatility', icon: 'Activity' }
          ]?.map(view => (
            <button
              key={view?.key}
              onClick={() => setViewType(view?.key)}
              className={`
                flex items-center gap-2 px-3 py-1 text-xs rounded-md transition-colors duration-200
                ${viewType === view?.key 
                  ? 'bg-primary text-primary-foreground' 
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }
              `}
            >
              <Icon name={view?.icon} size={12} />
              <span>{view?.label}</span>
            </button>
          ))}
        </div>

        {/* Current Stats */}
        {currentSpread && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
            <div className="text-center">
              <div className="text-muted-foreground">Current Spread</div>
              <div className="font-mono font-semibold text-foreground">
                {formatPrice(currentSpread?.spread)}
              </div>
            </div>
            <div className="text-center">
              <div className="text-muted-foreground">Basis Points</div>
              <div className="font-mono font-semibold text-foreground">
                {currentSpread?.spreadBps?.toFixed(1)} bps
              </div>
            </div>
            <div className="text-center">
              <div className="text-muted-foreground">Mid Price</div>
              <div className="font-mono font-semibold text-foreground">
                {formatPrice(currentSpread?.midPrice)}
              </div>
            </div>
            <div className="text-center">
              <div className="text-muted-foreground">Volatility</div>
              <div className="font-mono font-semibold text-foreground">
                {(currentSpread?.volatility * 100)?.toFixed(2)}%
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Chart */}
      <div className="p-4">
        <div className="h-64 w-full">
          <ResponsiveContainer width="100%" height="100%">
            {viewType === 'spread' ? (
              <AreaChart data={spreadData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis 
                  dataKey="time" 
                  stroke="var(--color-muted-foreground)"
                  fontSize={10}
                />
                <YAxis 
                  stroke="var(--color-muted-foreground)"
                  fontSize={10}
                  tickFormatter={(value) => formatPrice(value)}
                />
                <Tooltip content={<CustomTooltip />} />
                <Area
                  type="monotone"
                  dataKey="spread"
                  stroke="var(--color-accent)"
                  fill="var(--color-accent)"
                  fillOpacity={0.3}
                  strokeWidth={2}
                />
              </AreaChart>
            ) : viewType === 'midprice' ? (
              <LineChart data={spreadData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis 
                  dataKey="time" 
                  stroke="var(--color-muted-foreground)"
                  fontSize={10}
                />
                <YAxis 
                  stroke="var(--color-muted-foreground)"
                  fontSize={10}
                  tickFormatter={(value) => formatPrice(value)}
                />
                <Tooltip content={<CustomTooltip />} />
                <Line
                  type="monotone"
                  dataKey="bidPrice"
                  stroke="var(--color-success)"
                  strokeWidth={2}
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey="askPrice"
                  stroke="var(--color-error)"
                  strokeWidth={2}
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey="midPrice"
                  stroke="var(--color-accent)"
                  strokeWidth={2}
                  dot={false}
                  strokeDasharray="5 5"
                />
              </LineChart>
            ) : (
              <AreaChart data={spreadData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis 
                  dataKey="time" 
                  stroke="var(--color-muted-foreground)"
                  fontSize={10}
                />
                <YAxis 
                  stroke="var(--color-muted-foreground)"
                  fontSize={10}
                  tickFormatter={(value) => `${(value * 100)?.toFixed(1)}%`}
                />
                <Tooltip content={<CustomTooltip />} />
                <Area
                  type="monotone"
                  dataKey="volatility"
                  stroke="var(--color-warning)"
                  fill="var(--color-warning)"
                  fillOpacity={0.3}
                  strokeWidth={2}
                />
              </AreaChart>
            )}
          </ResponsiveContainer>
        </div>

        {/* Statistics Summary */}
        {spreadStats && (
          <div className="mt-4 p-3 bg-muted/30 rounded-lg">
            <h4 className="text-sm font-semibold text-foreground mb-2">
              {timeframe} Statistics
            </h4>
            <div className="grid grid-cols-3 gap-4 text-xs">
              <div>
                <span className="text-muted-foreground">Min Spread:</span>
                <div className="font-mono font-semibold text-foreground">
                  {formatPrice(spreadStats?.min)} ({spreadStats?.minBps?.toFixed(1)} bps)
                </div>
              </div>
              <div>
                <span className="text-muted-foreground">Avg Spread:</span>
                <div className="font-mono font-semibold text-foreground">
                  {formatPrice(spreadStats?.avg)} ({spreadStats?.avgBps?.toFixed(1)} bps)
                </div>
              </div>
              <div>
                <span className="text-muted-foreground">Max Spread:</span>
                <div className="font-mono font-semibold text-foreground">
                  {formatPrice(spreadStats?.max)} ({spreadStats?.maxBps?.toFixed(1)} bps)
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SpreadAnalysis;